// CustomAttribute 타입 정의
export interface CustomAttribute {
  'data-id': string;
  'data-description': string;
  [key: string]: any;
}

export const CUSTOM_ATTRIBUTES = {
  NAVIGATION_INDEX: {
    PIN_NAVIGATION: {
      'data-id': 'navigation-index-pin-button',
      'data-description': 'click: toggle navigation pin',
    } as CustomAttribute,

    DATA_REFRESH: {
      'data-id': 'navigation-index-data-refresh',
      'data-description': 'click: refresh data collection',
    } as CustomAttribute,

    HOME: {
      'data-id': 'navigation-index-home',
      'data-description': 'click: go to 홈',
    } as CustomAttribute,

    ACCOUNTS: {
      'data-id': 'navigation-index-accounts',
      'data-description': 'click: go to 계정 관리',
    } as CustomAttribute,

    PARTNERS: {
      'data-id': 'navigation-index-partners',
      'data-description': 'click: go to 거래처 관리',
    } as CustomAttribute,

    PROFIT_LOSS: {
      'data-id': 'navigation-index-profit-loss',
      'data-description': 'click: go to 손익 분석',
    } as CustomAttribute,

    MORE_MENU: {
      'data-id': 'navigation-index-more-menu',
      'data-description': 'click: open 더보기 메뉴',
    } as CustomAttribute,

    NOTIFICATIONS: {
      'data-id': 'navigation-index-notifications',
      'data-description': 'click: go to 알림',
    } as CustomAttribute,

    SUPPORT_INQUIRY: {
      'data-id': 'navigation-index-support-inquiry',
      'data-description': 'click: open 고객 지원',
    } as CustomAttribute,

    INTEGRATIONS: {
      'data-id': 'navigation-index-integrations',
      'data-description': 'click: go to 데이터 연동',
    } as CustomAttribute,

    FLOATING_NAVIGATION: {
      'data-id': 'navigation-index-floating-wrapper',
      'data-description': 'floating navigation panel',
    } as CustomAttribute,
  },

  NAVIGATION_BODY: {
    PIN_UNPIN_BUTTON: (isPinned: boolean | undefined) =>
      ({
        'data-id': 'navigation-body-pin-unpin-button',
        'data-description': isPinned ? 'click: unpin navigation' : 'click: pin navigation',
      }) as CustomAttribute,

    HOME_MENU: {
      'data-id': 'navigation-body-home-menu',
      'data-description': 'click: go to 홈',
    } as CustomAttribute,

    // 데이터 메뉴
    BANK_TRANSACTIONS: {
      'data-id': 'navigation-body-bank-transactions',
      'data-description': 'click: go to 은행 거래내역',
    } as CustomAttribute,

    CARD_APPROVALS: {
      'data-id': 'navigation-body-card-approvals',
      'data-description': 'click: go to 카드 승인',
    } as CustomAttribute,

    TAX_INVOICES: {
      'data-id': 'navigation-body-tax-invoices',
      'data-description': 'click: go to 세금계산서',
    } as CustomAttribute,

    CASH_RECEIPTS: {
      'data-id': 'navigation-body-cash-receipts',
      'data-description': 'click: go to 현금영수증',
    } as CustomAttribute,

    PG_MARKETPLACE: {
      'data-id': 'navigation-body-pg-marketplace',
      'data-description': 'click: go to PG/마켓플레이스 부가세 신고자료',
    } as CustomAttribute,

    // 수집 상태 텍스트
    COLLECTION_STATUS_TEXT: (scrapeTime?: string, status?: string) =>
      ({
        'data-id': 'data-collection-status',
        'data-description': `click: view details - collection status: ${
          status || 'unknown'
        }, last: ${scrapeTime || 'never'}`,
      }) as CustomAttribute,

    // 분석 메뉴
    ACCOUNTS_ANALYSIS: {
      'data-id': 'navigation-body-accounts-analysis',
      'data-description': 'click: go to 계정 분석',
    } as CustomAttribute,

    PARTNERS_ANALYSIS: {
      'data-id': 'navigation-body-partners-analysis',
      'data-description': 'click: go to 거래처 분석',
    } as CustomAttribute,

    PROFIT_LOSS_ANALYSIS: {
      'data-id': 'navigation-body-profit-loss-analysis',
      'data-description': 'click: go to 손익 분석',
    } as CustomAttribute,

    // 금융 메뉴
    CLOBE_FINANCE: {
      'data-id': 'navigation-body-clobe-finance',
      'data-description': 'click: go to 클로브 금융',
    } as CustomAttribute,
  },

  REFERRAL_EVENT: {
    'data-id': 'referral-event-menu',
    'data-description': 'click: go to 추천 이벤트',
  } as CustomAttribute,

  COMPANY_INFO_DROPDOWN: {
    'data-id': 'company-info-dropdown',
    'data-description': `Click to open workspace dropdown menu for switching workspaces, accessing company settings, managing members, creating new workspaces, user account settings, and logout`,
  } as CustomAttribute,

  // Company Dropdown Menu Items
  COMPANY_DROPDOWN_MENU: {
    COMPANY_INFO_MENU_ITEM: {
      'data-id': 'company-info-menu-item',
      'data-description': 'click: go to company settings',
    } as CustomAttribute,

    MEMBER_INFO_MENU_ITEM: {
      'data-id': 'member-info-menu-item',
      'data-description': 'click: go to member management',
    } as CustomAttribute,

    USER_SETTINGS_MENU_ITEM: {
      'data-id': 'user-settings-menu-item',
      'data-description': 'click: go to account settings',
    } as CustomAttribute,

    LOGOUT_MENU_ITEM: {
      'data-id': 'logout-menu-item',
      'data-description': 'click: logout',
    } as CustomAttribute,

    CREATE_WORKSPACE_BUTTON: {
      'data-id': 'create-workspace-button',
      'data-description': 'click: create workspace',
    } as CustomAttribute,

    COMPANY_SELECTOR: (companyId: string, companyName: string, isSelected: boolean) =>
      ({
        'data-id': `company-selector-${companyId}`,
        'data-description': `click: switch to ${companyName}${isSelected ? ' (selected)' : ''}`,
      }) as CustomAttribute,
  },

  // Bank Transactions Table
  BANK_TRANSACTIONS: {
    TABLE: {
      'data-id': 'bank-transactions-table',
      'data-description': '은행 거래내역 테이블',
    } as CustomAttribute,

    // Column Headers
    COLUMN_TRANSACTION_AT: {
      'data-id': 'bank-transactions-column-transaction-at',
      'data-description': '거래시간 header column - sortable',
    } as CustomAttribute,

    COLUMN_ACCOUNT: {
      'data-id': 'bank-transactions-column-account',
      'data-description': '계좌정보 header column',
    } as CustomAttribute,

    COLUMN_DESCRIPTION: {
      'data-id': 'bank-transactions-column-description',
      'data-description': '거래설명(적요) header column - sortable',
    } as CustomAttribute,

    COLUMN_INFLOW: {
      'data-id': 'bank-transactions-column-inflow',
      'data-description': '입금 header column - sortable',
    } as CustomAttribute,

    COLUMN_OUTFLOW: {
      'data-id': 'bank-transactions-column-outflow',
      'data-description': '출금 header column - sortable',
    } as CustomAttribute,

    COLUMN_BALANCE: {
      'data-id': 'bank-transactions-column-balance',
      'data-description': '거래 후 잔액 header column - sortable',
    } as CustomAttribute,

    COLUMN_TYPE: {
      'data-id': 'bank-transactions-column-type',
      'data-description': '거래 유형 header column - sortable',
    } as CustomAttribute,

    COLUMN_MEMO: {
      'data-id': 'bank-transactions-column-memo',
      'data-description':
        '메모 header column - editable. Double-click memo cell to activate input element, edit value, then press Enter to save memo changes',
    } as CustomAttribute,

    // Cell generators
    CELL_TRANSACTION_AT: (transactionId: number, date: string) =>
      ({
        'data-id': `bank-transactions-cell-date-${transactionId}`,
        'data-description': `거래시간: ${date}`,
      }) as CustomAttribute,

    CELL_ACCOUNT: (transactionId: number, accountNumber: string, accountType?: string) =>
      ({
        'data-id': `bank-transactions-cell-account-${transactionId}`,
        'data-description': `계좌: ${accountNumber}${accountType ? ` (${accountType})` : ''}`,
      }) as CustomAttribute,

    CELL_DESCRIPTION: (transactionId: number, description: string) =>
      ({
        'data-id': `bank-transactions-cell-description-${transactionId}`,
        'data-description': `적요: ${description}`,
      }) as CustomAttribute,

    CELL_INFLOW: (transactionId: number, amount?: number) =>
      ({
        'data-id': `bank-transactions-cell-inflow-${transactionId}`,
        'data-description': `입금: ${amount ? `${amount.toLocaleString()}원` : ''}`,
      }) as CustomAttribute,

    CELL_OUTFLOW: (transactionId: number, amount?: number) =>
      ({
        'data-id': `bank-transactions-cell-outflow-${transactionId}`,
        'data-description': `출금: ${amount ? `${amount.toLocaleString()}원` : ''}`,
      }) as CustomAttribute,

    CELL_BALANCE: (transactionId: number, balance?: number) =>
      ({
        'data-id': `bank-transactions-cell-balance-${transactionId}`,
        'data-description': `잔액: ${balance ? `${balance.toLocaleString()}원` : ''}`,
      }) as CustomAttribute,

    CELL_TYPE: (transactionId: number, type: string) =>
      ({
        'data-id': `bank-transactions-cell-type-${transactionId}`,
        'data-description': `유형: ${type}`,
      }) as CustomAttribute,

    CELL_MEMO: (transactionId: number, memo?: string) =>
      ({
        'data-id': `bank-transactions-cell-memo-${transactionId}`,
        'data-description': `memo: ${memo || 'NA'}, double-click to edit`,
      }) as CustomAttribute,
  },
};
